import React, { useCallback, useEffect, useRef, useState } from 'react';
import { askAssistant } from '../workforce/api/workforceApi';
import { Robot, User, PaperPlaneTilt } from '@phosphor-icons/react';

const SUGGESTIONS = [
  'How many open tickets do we have?',
  'Is anyone on the team free right now?',
  'Summarize ticket workload by domain',
  'How many users are pending approval?',
  'List active products and their teams',
  'What SLA policies are configured?',
  'Search knowledge base for VPN setup',
  'What assets are assigned vs available?',
];

function renderAnswer(text) {
  if (!text) return '';
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

function AiAssistantChat({ variant = 'page' }) {
  const isWidget = variant === 'widget';
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const chatScrollRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    requestAnimationFrame(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior, block: 'end' });
      } else if (chatScrollRef.current) {
        chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
      }
    });
  }, []);

  useEffect(() => {
    scrollToBottom(messages.length <= 2 ? 'auto' : 'smooth');
  }, [messages, loading, scrollToBottom]);

  const send = async (q) => {
    const text = (q || question).trim();
    if (!text || loading) return;
    setQuestion('');
    setMessages((prev) => [...prev, { role: 'user', text, id: Date.now() }]);
    setLoading(true);
    scrollToBottom('auto');
    try {
      const res = await askAssistant(text);
      setMessages((prev) => [...prev, {
        id: Date.now() + 1,
        role: 'assistant',
        text: res.answer,
      }]);
    } catch (e) {
      setMessages((prev) => [...prev, {
        id: Date.now() + 1,
        role: 'assistant',
        text: e.response?.data?.detail || 'Query failed',
        error: true,
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const widgetSuggestions = SUGGESTIONS.slice(0, 4);

  return (
    <div style={isWidget ? widgetRootStyle : pageRootStyle}>
      {!isWidget && (
        <div style={headerStyle}>
          <div>
            <h1 style={{ fontSize: '15px', fontWeight: '700', margin: '0 0 4px', color: '#111827' }}>
              AI Assistant
            </h1>
            <p style={{ color: '#6B7280', fontSize: '12px', margin: 0 }}>
              Ask anything across EasyITSM — tickets, users, products, assets, knowledge, SLA, and more.
            </p>
          </div>
        </div>
      )}

      <div style={isWidget ? widgetChatCardStyle : chatCardStyle}>
        <div ref={chatScrollRef} style={isWidget ? widgetMessagesAreaStyle : messagesAreaStyle}>
          {messages.length === 0 && !loading && (
            <div style={isWidget ? widgetEmptyStateStyle : emptyStateStyle}>
              <div style={isWidget ? widgetEmptyIconWrap : emptyIconWrap}>
                <Robot size={isWidget ? 24 : 32} weight="duotone" color="#002FA7" />
              </div>
              <p style={{ fontSize: isWidget ? '14px' : '15px', fontWeight: '600', color: '#374151', margin: '0 0 6px' }}>
                How can I help you today?
              </p>
              <p style={{
                fontSize: '12px',
                color: '#9CA3AF',
                margin: 0,
                maxWidth: isWidget ? '280px' : '360px',
                lineHeight: 1.5,
              }}
              >
                {isWidget
                  ? 'Ask about tickets, users, assets, knowledge, and more.'
                  : 'Universal ITSM copilot — ask about any module you have access to without navigating the sidebar.'}
              </p>
            </div>
          )}

          {messages.map((msg) => (
            <div
              key={msg.id}
              style={{
                display: 'flex',
                gap: '10px',
                marginBottom: isWidget ? '12px' : '16px',
                flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                alignItems: 'flex-start',
              }}
            >
              <div style={{
                ...avatarStyle,
                width: isWidget ? '28px' : '32px',
                height: isWidget ? '28px' : '32px',
                background: msg.role === 'user' ? '#002FA7' : (msg.error ? '#FEE2E2' : '#EFF6FF'),
                color: msg.role === 'user' ? 'white' : (msg.error ? '#DC2626' : '#002FA7'),
                flexShrink: 0,
              }}
              >
                {msg.role === 'user'
                  ? <User size={isWidget ? 14 : 16} weight="bold" />
                  : <Robot size={isWidget ? 14 : 16} weight="bold" />}
              </div>
              <div style={{ maxWidth: isWidget ? '82%' : '78%', minWidth: 0 }}>
                <div style={{
                  padding: isWidget ? '10px 12px' : '12px 14px',
                  borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  fontSize: isWidget ? '13px' : '14px',
                  lineHeight: 1.55,
                  background: msg.role === 'user' ? '#002FA7' : (msg.error ? '#FEE2E2' : 'white'),
                  color: msg.role === 'user' ? 'white' : (msg.error ? '#991B1B' : '#111827'),
                  border: msg.role === 'assistant' ? '1px solid #E5E7EB' : 'none',
                  boxShadow: msg.role === 'assistant' ? '0 1px 2px rgba(0,0,0,0.04)' : 'none',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
                >
                  {msg.role === 'assistant' && !msg.error ? renderAnswer(msg.text) : msg.text}
                </div>
              </div>
            </div>
          ))}

          {loading && (
            <div style={{ display: 'flex', gap: '10px', marginBottom: isWidget ? '12px' : '16px', alignItems: 'flex-start' }}>
              <div style={{
                ...avatarStyle,
                width: isWidget ? '28px' : '32px',
                height: isWidget ? '28px' : '32px',
                background: '#EFF6FF',
                color: '#002FA7',
              }}
              >
                <Robot size={isWidget ? 14 : 16} weight="bold" />
              </div>
              <div style={{
                padding: '6px 10px',
                borderRadius: '16px 16px 16px 4px',
                background: 'white',
                border: '1px solid #E5E7EB',
                display: 'flex',
                gap: '5px',
                alignItems: 'center',
              }}
              >
                <span className="ai-typing-dot" />
                <span className="ai-typing-dot" />
                <span className="ai-typing-dot" />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} style={{ height: 1, flexShrink: 0 }} />
        </div>

        {messages.length === 0 && (
          <div style={isWidget ? widgetSuggestionsWrapStyle : suggestionsWrapStyle}>
            {(isWidget ? widgetSuggestions : SUGGESTIONS).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => send(s)}
                disabled={loading}
                style={isWidget ? widgetSuggestionBtnStyle : suggestionBtnStyle}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          style={isWidget ? widgetInputBarStyle : inputBarStyle}
        >
          <textarea
            ref={inputRef}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about tickets, team status, or ITSM guidance…"
            rows={1}
            disabled={loading}
            style={isWidget ? widgetTextareaStyle : textareaStyle}
          />
          <button
            type="submit"
            disabled={loading || !question.trim()}
            style={{
              ...(isWidget ? widgetSendBtnStyle : sendBtnStyle),
              opacity: loading || !question.trim() ? 0.5 : 1,
              cursor: loading || !question.trim() ? 'not-allowed' : 'pointer',
            }}
            title="Send (Enter)"
          >
            <PaperPlaneTilt size={isWidget ? 18 : 20} weight="fill" />
          </button>
        </form>
      </div>
    </div>
  );
}

const pageRootStyle = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  maxWidth: '880px',
  margin: '0 auto',
  width: '100%',
};

const widgetRootStyle = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  height: '100%',
};

const headerStyle = {
  marginBottom: '8px',
  flexShrink: 0,
};

const chatCardStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  background: 'white',
  borderRadius: '12px',
  border: '1px solid #E5E7EB',
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  overflow: 'hidden',
};

const widgetChatCardStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  background: 'white',
  overflow: 'hidden',
};

const messagesAreaStyle = {
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: '10px',
  background: '#F9FAFB',
  scrollBehavior: 'smooth',
};

const widgetMessagesAreaStyle = {
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: '10px',
  background: '#F9FAFB',
  scrollBehavior: 'smooth',
};

const emptyStateStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: '48px 24px 32px',
};

const widgetEmptyStateStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: '32px 16px 24px',
};

const emptyIconWrap = {
  width: '64px',
  height: '64px',
  borderRadius: '50%',
  background: '#EFF6FF',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '8px',
};

const widgetEmptyIconWrap = {
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  background: '#EFF6FF',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '6px',
};

const avatarStyle = {
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const suggestionsWrapStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  padding: '6px 10px',
  borderTop: '1px solid #E5E7EB',
  background: 'white',
};

const widgetSuggestionsWrapStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  padding: '6px 8px',
  borderTop: '1px solid #E5E7EB',
  background: 'white',
};

const suggestionBtnStyle = {
  padding: '7px 12px',
  fontSize: '12px',
  borderRadius: '999px',
  border: '1px solid #BFDBFE',
  background: '#EFF6FF',
  color: '#1E40AF',
  cursor: 'pointer',
  lineHeight: 1.4,
  textAlign: 'left',
};

const widgetSuggestionBtnStyle = {
  padding: '4px 10px',
  fontSize: '12px',
  borderRadius: '8px',
  border: '1px solid #BFDBFE',
  background: '#EFF6FF',
  color: '#1E40AF',
  cursor: 'pointer',
  lineHeight: 1.4,
  textAlign: 'left',
};

const inputBarStyle = {
  display: 'flex',
  gap: '10px',
  alignItems: 'flex-end',
  padding: '14px 16px',
  borderTop: '1px solid #E5E7EB',
  background: 'white',
  flexShrink: 0,
};

const widgetInputBarStyle = {
  display: 'flex',
  gap: '8px',
  alignItems: 'flex-end',
  padding: '12px',
  borderTop: '1px solid #E5E7EB',
  background: 'white',
  flexShrink: 0,
};

const textareaStyle = {
  flex: 1,
  padding: '8px 10px',
  borderRadius: '10px',
  border: '1px solid #D1D5DB',
  fontSize: '12px',
  fontFamily: 'inherit',
  lineHeight: 1.5,
  resize: 'none',
  minHeight: '44px',
  maxHeight: '120px',
  outline: 'none',
};

const widgetTextareaStyle = {
  flex: 1,
  padding: '6px 8px',
  borderRadius: '10px',
  border: '1px solid #D1D5DB',
  fontSize: '12px',
  fontFamily: 'inherit',
  lineHeight: 1.5,
  resize: 'none',
  minHeight: '40px',
  maxHeight: '80px',
  outline: 'none',
};

const sendBtnStyle = {
  width: '44px',
  height: '44px',
  background: '#002FA7',
  color: 'white',
  border: 'none',
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const widgetSendBtnStyle = {
  width: '40px',
  height: '40px',
  background: '#002FA7',
  color: 'white',
  border: 'none',
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

export default AiAssistantChat;
